import React, { useState } from 'react';
import { Link } from "react-router-dom";

import DomainReference from '../../components/domainReference.js'
import TechnologyIconAndName from '../../components/technologyIconAndName.js';
import VersionRef from '../../components/versionRef.js'
import WebsiteSecondaryPageTitle from '../../components/websiteSecondaryPageTitle.js'
import UserError from '../../components/userError.js';

import { _printDate } from '../../helpers/helpers.js'

import {RtTechnologyCategory, RtTechnologyApp, RtDomainWithTechnology, AggregatedDomainPageResult} from '../../../../server/routes/website.js'


interface WebsiteAggregatedOptions {
    server: string; 
    pageData: unknown;
}

export default function WebsiteAggregated({ server, pageData }: WebsiteAggregatedOptions) {

	let [data, setData] = useState(pageData as AggregatedDomainPageResult);

    return (<>

        <WebsiteSecondaryPageTitle domainPageHeader={data.DomainPageHeader}/>
        <section className="section section-sm">
            <div className="container">
                <div className="row row-50">
                    <div className="col-md-8 col-lg-8 col-xl-8">
                        <WebsiteAggregatedLeftPanel props={data}/>
                    </div>
                    <div className="col col-md-4 col-lg-4 col-xl-4">
                        <WebsiteAggregatedRightPanel props={data}/>
                    </div>
                </div>
            </div>
        </section>

    </>)
}

function WebsiteAggregatedLeftPanel({ props }: { props: AggregatedDomainPageResult }) {

    return (<>

        <UserError message={props.Warning}/>		
        {
            props.AggregatedTechnologies.map((aggregatedCategory, indexKey) =>
                <AggregatedCategory aggregatedCategory={aggregatedCategory} expandFirstTechnology={0 == indexKey} key={indexKey}/>)
        }

    </>)
}

function AggregatedCategory({ aggregatedCategory, expandFirstTechnology }: { aggregatedCategory: RtTechnologyCategory; expandFirstTechnology: boolean }) {

    const wtsBottomMargin = {
        marginBottom: '20px'
    }

    return (<>

        <div className="card card-body" style={wtsBottomMargin}>
            <span>
                <span className={`icon text-primary mdi ${aggregatedCategory.CategoryIcon}`}></span>
                {' '}<b>{aggregatedCategory.Category}</b>
            </span>
            <div>
                {
                    aggregatedCategory.Apps.map((aggregatedTechnology, indexKey) => 
                        <AggregatedTechnology aggregatedTechnology={aggregatedTechnology} collapse={!expandFirstTechnology || 0 != indexKey} key={indexKey}/>
                    )
                }
            </div>
        </div>

    </>)

}

function CollapseButton({ collapsed: collapsedProp, onChange }: { collapsed: boolean; onChange: (collapsed: boolean) => void }) {
    const [collapsed, setCollapsed] = useState<boolean>(collapsedProp);

    function handleOnClick() {
        setCollapsed(!collapsed); // maybe should use callback version here setCollapsed((prevValue) => {})
        onChange(!collapsed);
    }

    return (
        <span onClick={handleOnClick} style={{cursor:'pointer'}}>
            <b>{collapsed?'+':'-'}</b>
        </span>
    )
}

function AggregatedTechnology({ aggregatedTechnology, collapse: collapseProp }: { aggregatedTechnology: RtTechnologyApp; collapse: boolean }) {

    const [collapsed, setCollapsed] = useState<boolean>(collapseProp);
    const wtsNoBorder = {
        border: 'none'
    };

    function handleCollapseToggle(collapsed: boolean): void {
        setCollapsed(collapsed);
    }

    const domainCount = aggregatedTechnology.Domains.length;
    let usedSince = aggregatedTechnology.Domains[0].UsedSince; 
    aggregatedTechnology.Domains.forEach((domain) => {
        if (usedSince > domain.UsedSince) {
            usedSince = domain.UsedSince;
        }
    }) 

    return (<>

        <div className="nope-not-a-container">
            <div className="card card-body" style={wtsNoBorder}>
                <span>
                    <CollapseButton collapsed={collapsed} onChange={handleCollapseToggle} />
                    {' '}
                    <TechnologyIconAndName 
                        technology={aggregatedTechnology.Technology}
                        technologyURL={aggregatedTechnology.TechnologyURL}
                        technologyIcon={aggregatedTechnology.TechnologyIcon}
                        title={`${aggregatedTechnology.Technology} market share and usage statistic`}
                    />
                    {
                        collapsed&&
                        <>
                            {'  '} {domainCount==1?'one':domainCount}{' '}subdomain{domainCount!=1&&'s'}, used since {_printDate(usedSince)}
                        </>
                    }
                </span>
                {
                    !collapsed &&
                    <div>
                        <div className="card card-body" style={wtsNoBorder}>
                            <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                                <ul>
                                    {
                                        aggregatedTechnology.Domains.map((aggregatedDomain, indexKey) => 
                                            <AggregatedDomain 
                                                key={indexKey}
                                                aggregatedDomain={aggregatedDomain}
                                                technology={aggregatedTechnology.Technology}
                                                technologyURL={aggregatedTechnology.TechnologyURL}
                                                />
                                        )
                                    }
                                </ul>
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>  
    </>)
}

function AggregatedDomain({ aggregatedDomain, technology, technologyURL }: { aggregatedDomain: RtDomainWithTechnology; technology: string; technologyURL: string }) {

    return (<>
        <li>
            <DomainReference 
                domain={aggregatedDomain.Domain.Domain}
                openProtocol={aggregatedDomain.Domain.OpenProtocol}
                title={`${aggregatedDomain.Domain.Domain} information and technology stack`}
            />
            {' '},
            {
                aggregatedDomain.Version &&
                <>
                    Version{' '}
                    <VersionRef 
                        technology= {{
                            TechnologyURL:technologyURL,
                            Technology:technology,
                            Version:aggregatedDomain.Version
                    }}/>
                    ,
                </>
            }
            {' '}Used since {_printDate(aggregatedDomain.UsedSince)}
        </li>    
    </>)
}

function WebsiteAggregatedRightPanel({ props }: { props: AggregatedDomainPageResult }) {

    return (<>

        <h3>Subdomains</h3>
        <br/>
        <div>
            {
                props.Subdomains.Count > 0 ?
                <ul>
                    {
                        props.Subdomains.Domains.map((subdomain, indexKey) =>
                            <li key={indexKey}>
                                <DomainReference 
                                    domain={subdomain.Domain}
                                    openProtocol={subdomain.OpenProtocol}
                                    title={`${subdomain.Domain} information and technology stack`}
                                />
                            </li>
                        )
                    }
                    {
                        props.Subdomains.MoreThan10 &&
                        <>
                            <br/>
                            <li>
                                <Link 
                                    to={`/website/${props.SecondLevelDomain}/subdomains`} 
                                    className="link-hover" 
                                    title={`${props.SecondLevelDomain} subdomain list`}
                                > 
                                    ... See all {props.Subdomains.Count} 
                                </Link>
                            </li>
                        </>
                    }
                </ul> :
                <>
                No subdomains
                </>
            }
        </div>
        <br/>

    </>)
}

